import axios from 'axios';
import { baseURL } from '../Constants';

export interface AvailableMap {
    id: number;
    mapName: string;
    internalName: string;
    imageUrl: string;
    isCompetitive: boolean;
    isHistoric: boolean;
    displayOrder: number;
    createdAt: string;
}

export interface SeasonMappool {
    id: number;
    season: number;
    name: string;
    maps: string[];
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
}

export interface UpdateSeasonMappoolRequest {
    season: number;
    name: string;
    maps: string[];
    isActive: boolean;
}

class MappoolServiceClass {
    private readonly apiBaseUrl: string;

    constructor() {
        this.apiBaseUrl = `${baseURL}/mappool`;
    }

    // Get mappool for a specific season
    async fetchSeasonMappool(season: number): Promise<SeasonMappool> {
        try {
            const response = await axios.get<SeasonMappool>(`${this.apiBaseUrl}/season/${season}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching mappool for season ${season}:`, error);
            throw error;
        }
    }

    // Get the currently active mappool
    async fetchActiveMappool(): Promise<SeasonMappool> {
        try {
            const response = await axios.get<SeasonMappool>(`${this.apiBaseUrl}/active`);
            return response.data;
        } catch (error) {
            console.error('Error fetching active mappool:', error);
            throw error;
        }
    }

    // Get all season mappools (admin only)
    async fetchAllMappools(authToken: string): Promise<SeasonMappool[]> {
        try {
            const response = await axios.get<SeasonMappool[]>(`${this.apiBaseUrl}/all`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching all mappools:', error);
            throw error;
        }
    }

    // Get all available maps (admin only)
    async fetchAvailableMaps(authToken: string): Promise<AvailableMap[]> {
        try {
            const response = await axios.get<AvailableMap[]>(`${this.apiBaseUrl}/available-maps`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching available maps:', error);
            throw error;
        }
    }

    // Update or create a season mappool (admin only)
    async updateSeasonMappool(
        season: number,
        request: UpdateSeasonMappoolRequest,
        authToken: string
    ): Promise<SeasonMappool> {
        try {
            const response = await axios.post<{ message: string; mappool: SeasonMappool }>(
                `${this.apiBaseUrl}/season/${season}`,
                request,
                {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            return response.data.mappool;
        } catch (error) {
            console.error(`Error updating mappool for season ${season}:`, error);
            throw error;
        }
    }

    // Invalidate cache (admin only)
    async invalidateCache(authToken: string): Promise<void> {
        try {
            await axios.post(
                `${this.apiBaseUrl}/invalidate-cache`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                }
            );
        } catch (error) {
            console.error('Error invalidating mappool cache:', error);
            throw error;
        }
    }
}

export const MappoolService = new MappoolServiceClass();
